/**
 * Lazy-loaded Monaco editor wrapper for the unified Tests page (#542).
 *
 * Monaco is ~300KB+ minified — too heavy to ship in the initial
 * bundle for every page load. We dynamic-import `@monaco-editor/react`
 * via `React.lazy()` so the editor only loads when a user actually
 * opens the Tests page AND the script pane is rendered. The
 * `<Suspense>` fallback below renders a plain `<pre>` of the current
 * value so the user sees their code immediately while Monaco is
 * fetching.
 *
 * **Worker strategy.** `@monaco-editor/react` defaults to loading
 * Monaco from a CDN (jsdelivr) — no Vite worker config needed.
 * First load triggers the CDN fetch; subsequent renders are
 * instant. For an admin/dev surface this trade-off is fine; if we
 * later need offline / air-gapped support, switch to the
 * vite-monaco-editor plugin and self-host the workers.
 *
 * **Language awareness.** `beforeMount` configures Monaco's TypeScript
 * service with a curated Playwright type-shim (see
 * `monacoPlaywrightSetup`), which lights up autocomplete, hover types,
 * and signature help for the `getByRole` / `expect(...)` API our
 * generator emits. This is intentionally an in-editor convenience —
 * NOT a step toward an in-browser IDE. Anyone who wants the full
 * Playwright extension (Test Explorer, debugger, record) still uses
 * their own VS Code; OttoTester's job is autonomous testing, and the
 * embedded editor stays a lightweight read/edit surface.
 *
 * Props match the subset of the underlying `<Editor>` API we
 * actually use — keep the surface narrow so the next maintainer
 * doesn't accidentally rely on Monaco specifics that bind us to
 * this editor choice.
 */
import { lazy, Suspense, useEffect, useState } from 'react'
import { configureMonacoForPlaywright, isPlaywrightLanguage } from './monacoPlaywrightSetup'

const Editor = lazy(() => import('@monaco-editor/react'))

/** Tracks whether the app is currently in dark mode by observing
 * the `dark` class on `<html>`. The app's `useTheme` hook
 * adds/removes that class for explicit user choice or system
 * preference; this hook just mirrors it into React state so Monaco
 * can switch its theme without a page reload. */
function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  )
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    const update = () => setIsDark(root.classList.contains('dark'))
    update()
    const observer = new MutationObserver(update)
    observer.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return isDark
}

export interface MonacoCodePaneProps {
  value: string
  onChange?: (next: string) => void
  /** Read-only when true. In view mode the editor still renders
   * with syntax highlighting + folding, just no caret / edits. */
  readOnly?: boolean
  /** Monaco language id. We always pass 'typescript' today —
   * Playwright `.spec.ts` files are TypeScript. Kept configurable
   * so a future test framework could swap it without touching this
   * wrapper. */
  language?: string
  /** Container height. The page sets this to fill its split-pane
   * column. Defaults to `100%`. */
  height?: string
}

export function MonacoCodePane({
  value,
  onChange,
  readOnly = false,
  language = 'typescript',
  height = '100%',
}: MonacoCodePaneProps) {
  // Track the app's dark/light state and pick the matching Monaco
  // built-in theme. `vs` is Monaco's light, `vs-dark` is its dark —
  // both built in, no extra loading. Editor re-themes on the next
  // render when isDark flips.
  const isDark = useIsDarkMode()
  return (
    <Suspense
      fallback={
        <pre className="h-full overflow-auto p-3 text-xs font-mono bg-muted/30 whitespace-pre-wrap">
          {value}
          <span className="block mt-2 text-muted-foreground italic">Loading editor…</span>
        </pre>
      }
    >
      <Editor
        height={height}
        language={language}
        value={value}
        theme={isDark ? 'vs-dark' : 'vs'}
        beforeMount={(monaco) => {
          if (isPlaywrightLanguage(language)) configureMonacoForPlaywright(monaco)
        }}
        onChange={(v) => onChange?.(v ?? '')}
        options={{
          readOnly,
          fontSize: 12,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          // Line numbers ON — the side-by-side layout is narrow
          // but Playwright spec failures cite line numbers in
          // error messages, so an editor without them would force
          // the user to manually count when cross-referencing.
          // The minimap is the bigger space-saver and stays off.
          lineNumbers: 'on',
          // Subtle active-line highlight helps locate the caret in the
          // narrow pane without the heavier 'all' gutter+margin tint.
          renderLineHighlight: 'line',
          smoothScrolling: true,
          // Keep the enclosing test.describe / test(...) header pinned
          // while scrolling a long spec — never lose which test you're
          // reading.
          stickyScroll: { enabled: true },
          // Matching brackets share a color + nesting guides — makes
          // Playwright's chained calls and nested { name } options far
          // easier to scan.
          bracketPairColorization: { enabled: true },
          guides: { bracketPairs: true, indentation: true },
          // URLs in generated comments (e.g. playwright.dev links)
          // become clickable.
          links: true,
          // Language-aware affordances, powered by the Playwright shim
          // wired in `beforeMount`: autocomplete, trigger-char
          // suggestions, and signature help while editing.
          quickSuggestions: true,
          suggestOnTriggerCharacters: true,
          parameterHints: { enabled: true },
          folding: true,
          glyphMargin: false,
        }}
      />
    </Suspense>
  )
}
